// The experience filter's values and labels, shared by the search bar's Experience
// select and the mobile summary line that reads its choice back. Values match what
// TypeFilter.vue and useSearch.js already key results by - not
// search-form.blade.php's own wording (any/spa-treatments/spa-days/spa-breaks).
//
// TypeFilter.vue holds its own copy of these labels alongside an icon per type. It
// predates this module and is deliberately not folded in here. The ORDER below is shared.
//
// 'seasonal' is not a product type: it lists every product flagged is_seasonal, whatever
// its type. See useSearch.js's matchesType().
export const EXPERIENCE_OPTIONS = [
    { value: '', label: 'Any' },
    { value: 'treatment', label: 'Treatments' },
    { value: 'spa-day', label: 'Spa Days' },
    { value: 'spa-offer', label: 'Spa Offers' },
    { value: 'spa-break', label: 'Spa Breaks' },
    { value: 'seasonal', label: 'Seasonal' },
];

// The modes that offer Seasonal. Adding 'book' enables it there, along with the steps in
// docs/feature-requests/seasonal-filter-scope-technical.md section 7.
const SEASONAL_MODES = ['gift'];

export function offersSeasonal(mode) {
    return SEASONAL_MODES.includes(mode);
}

// The order the Experience select and the results page's type chips list their options in,
// per mode. Values only; each consumer filters out what does not apply.
const ORDER = {
    book: ['', 'seasonal', 'treatment', 'spa-day', 'spa-offer', 'spa-break'],
    gift: ['', 'seasonal', 'spa-day', 'spa-break', 'treatment', 'spa-offer'],
};

// Sorts items by their value's position in the mode's order. `valueOf` reads the value from
// an item, since the select's options and the chips' tabs are shaped differently.
export function inExperienceOrder(items, mode, valueOf) {
    const order = ORDER[mode] ?? ORDER.book;

    return [...items].sort((a, b) => order.indexOf(valueOf(a)) - order.indexOf(valueOf(b)));
}

// The party size an experience is sold at, where the data allows only one. A treatment is
// only ever returned for a single guest (ProductSearchService gates it in code), and every
// spa break is a two-guest product (all 19 live rows). Spa days and spa offers sell at both,
// so they are absent - they impose nothing.
//
// Two readers, which is why it lives here rather than in either of them: SearchBar applies
// it when an Experience is picked, and TypeFilter hides a chip that cannot apply at the
// guest count on screen. If a spa break at another size is ever added, this is the one place
// to say so.
export const GUESTS_REQUIRED_BY_EXPERIENCE = {
    'spa-break': 2,
    treatment: 1,
};

// Whether an experience can return anything at this guest count. Fails open on an unknown
// count: the guest field offers 1-2, matching the catalogue, but a hand-edited or older
// shared ?people= can still carry more, and at 3+ every type is genuinely empty - hiding
// them all would read as a broken page rather than an empty result. Any ('') is always
// available - it imposes no size of its own.
export function experienceAllowsGuests(value, people) {
    const required = GUESTS_REQUIRED_BY_EXPERIENCE[value];

    if (! required || ! people) return true;

    return required === people;
}

// The label for a committed value, or null for Any and for anything unrecognised.
// The mobile summary shows only real choices, so Any correctly reads as no choice.
export function experienceLabel(value) {
    if (! value) return null;

    return EXPERIENCE_OPTIONS.find(option => option.value === value)?.label ?? null;
}
